<template>
  <div class="xo-page" v-loading="loading">
    <div class="xo-page-header">
      <div class="xo-inner">
        <div class="xo-titlebar">
          <div>
            <h1 class="xo-title">Statement</h1>
            <p class="xo-subtitle">{{ resultSummary }}</p>
          </div>
          <button class="xo-btn xo-btn--navy" @click="downloadCSV">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              style="flex-shrink: 0"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              />
              <polyline points="14 2 14 8 20 8" />
              <line x1="8" y1="13" x2="16" y2="13" />
              <line x1="8" y1="17" x2="16" y2="17" />
              <line x1="10" y1="9" x2="14" y2="9" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>
    </div>

    <div class="xo-inner pg-list-wrap">
      <!-- Pending-hold strip — surfaces funds that have been debited from
           the customer's account but are being held until the delivery
           documents pass verification. Only rendered when the accounting
           service reports at least one order in the hold state; otherwise
           the strip is silent so the page stays quiet in its default
           state. Sits above the statement table so the reason for any
           "balance vs statement total" discrepancy is immediately visible. -->
      <div
        v-if="pendingHold.orders > 0"
        class="pg-hold-strip"
        role="status"
      >
        <div class="pg-hold-strip__icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div class="pg-hold-strip__body">
          <span class="pg-hold-strip__amount">KES {{ pendingHold.amount.toLocaleString() }}</span>
          <span class="pg-hold-strip__label">held pending doc verification</span>
        </div>
        <span class="pg-hold-strip__count">
          {{ pendingHold.orders }} {{ pendingHold.orders === 1 ? 'order' : 'orders' }}
        </span>
      </div>

      <div class="pg-table">
        <div class="pg-thead">
          <div class="pg-th pg-th--txn">Transaction</div>
          <div class="pg-th pg-th--date">Date</div>
          <div class="pg-th pg-th--type">Type</div>
          <div class="pg-th pg-th--desc">Description</div>
          <div class="pg-th pg-th--amount">Amount</div>
          <div class="pg-th pg-th--balance">Balance</div>
        </div>

        <div class="pg-tbody">
          <div v-for="(row, i) in paginatedData" :key="i" class="pg-row-wrap">
            <div class="pg-row">
              <div class="pg-td pg-td--txn">
                <span class="pg-ref">{{ row.txn }}</span>
              </div>
              <div class="pg-td pg-td--date">
                <span class="pg-td-muted">{{
                  transformDate(row.date_time)
                }}</span>
              </div>
              <div class="pg-td pg-td--type">
                <span class="pg-type-badge" :class="getTypeClass(row.type)">{{
                  row.type
                }}</span>
              </div>
              <div class="pg-td pg-td--desc">
                <span class="pg-td-text">{{ row.description }}</span>
              </div>
              <div class="pg-td pg-td--amount">
                <span
                  class="pg-amount"
                  :class="row.amount < 0 ? 'pg-amount--neg' : 'pg-amount--pos'"
                >
                  KES {{ Math.abs(row.amount).toLocaleString() }}
                </span>
              </div>
              <div class="pg-td pg-td--balance">
                <span class="pg-balance"
                  >KES {{ row.running_balance.toLocaleString() }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="xo-pager" v-if="!loading && totalPages > 1">
        <div class="xo-pager-info">
          Showing <strong>{{ rangeStart }}–{{ rangeEnd }}</strong> of <strong>{{ tableData.data.length }}</strong>
        </div>
        <div class="xo-pager-controls">
          <button class="xo-pager-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)" aria-label="Previous page">‹</button>
          <template v-for="(p, idx) in pageButtons" :key="idx">
            <span v-if="p === '…'" class="xo-pager-ellipsis">…</span>
            <button v-else class="xo-pager-btn" :class="{ 'xo-pager-btn--active': p === currentPage }" @click="goToPage(p as number)">{{ p }}</button>
          </template>
          <button class="xo-pager-btn" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)" aria-label="Next page">›</button>
        </div>
      </div>

      <div class="pg-empty" v-if="!loading && tableData.data.length === 0">
        <div class="pg-empty-icon">📄</div>
        <div class="pg-empty-text">No statement entries found</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, computed, watch } from "vue";
import router from "../../router";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";
import axios from "axios";
import { DateTime } from "luxon";
import { unparse } from "papaparse";
import { isCompanyAdminFromToken } from "../../utils/authRole";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

const ensureAdminAccess = () => {
  if (!isCompanyAdminFromToken(localStorage.getItem("_panda"))) {
    router.replace("/request");
    return false;
  }
  return true;
};

const loading = ref(false);
const tableData = reactive({ data: <any>[] });

// Client-side pagination — 10 rows per page. CSV export still grabs the full set.
const PAGE_SIZE = 10;
const currentPage = ref(1);
const totalPages = computed(() => Math.max(1, Math.ceil(tableData.data.length / PAGE_SIZE)));
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return tableData.data.slice(start, start + PAGE_SIZE);
});
watch(totalPages, (n) => { if (currentPage.value > n) currentPage.value = n; });

const goToPage = (n: number) => {
  if (n < 1 || n > totalPages.value) return;
  currentPage.value = n;
  if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
};

const pageButtons = computed<(number | "…")[]>(() => {
  const total = totalPages.value;
  const cur = currentPage.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  const left = Math.max(2, cur - 1);
  const right = Math.min(total - 1, cur + 1);
  if (left > 2) out.push("…");
  for (let i = left; i <= right; i++) out.push(i);
  if (right < total - 1) out.push("…");
  out.push(total);
  return out;
});

const rangeStart = computed(() =>
  tableData.data.length === 0 ? 0 : (currentPage.value - 1) * PAGE_SIZE + 1,
);
const rangeEnd = computed(() =>
  Math.min(currentPage.value * PAGE_SIZE, tableData.data.length),
);

// Header subtitle — same count line every list page carries.
const resultSummary = computed(() => {
  if (loading.value) return "Loading statement…";
  const total = tableData.data.length;
  return `${total} ${total === 1 ? "entry" : "entries"}`;
});

const postData = async () => {
  if (!ensureAdminAccess()) return;
  loading.value = true;
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_ACCOUNTING_SERVICE}customer_statement`,
      {
        company_id: store.getUserData.company_id,
        limit: 30,
        token: store.getToken,
      },
    );
    if (response.status == 200) tableData.data = response.data;
    else ElMessage({ message: "Failed to load statement.", type: "error" });
  } catch {
    ElMessage({ message: "Failed to load statement.", type: "error" });
  }
  loading.value = false;
};
void postData();

// ── Pending-hold strip ─────────────────────────────────────────
// Independent of the statement fetch — the two endpoints answer
// different questions ("what has moved" vs "what is on hold") and one
// failing shouldn't blank the other. Runs in the background; the strip
// renders only after the response comes back with pending_orders > 0.
//
// Accounting service returns `pending_debit` for the customer side
// (mirror endpoint `/driver_pending_hold` returns `pending_credit`).
const pendingHold = reactive({ amount: 0, orders: 0 });

const loadPendingHold = async () => {
  try {
    const res = await axios.post(
      `${import.meta.env.VITE_ACCOUNTING_SERVICE}customer_pending_hold`,
      {
        company_id: store.getUserData.company_id,
        token: store.getToken,
      },
    );
    const payload = res?.data ?? {};
    // Support either wrapping shape — some endpoints in this codebase
    // return the payload flat, others under a `data` key.
    const body = payload.data ?? payload;
    pendingHold.amount = Number(body.pending_debit ?? 0);
    pendingHold.orders = Number(body.pending_orders ?? 0);
  } catch {
    // Silent — the strip is an enhancement; if the endpoint is down
    // the statement page still works.
    pendingHold.amount = 0;
    pendingHold.orders = 0;
  }
};
void loadPendingHold();

const transformDate = (date: string) =>
  DateTime.fromISO(date).toFormat("dd MMM yyyy, HH:mm");

const getTypeClass = (type: string) => {
  if (!type) return "";
  const t = type.toLowerCase();
  if (t.includes("credit") || t.includes("payment")) return "pg-type--credit";
  if (t.includes("debit") || t.includes("charge")) return "pg-type--debit";
  return "pg-type--default";
};

const downloadCSV = () => {
  if (!tableData.data.length) {
    ElMessage({ message: "No data to download", type: "warning" });
    return;
  }
  const csv = unparse(
    tableData.data.map((row: any) => ({
      Transaction: row.txn,
      Date: transformDate(row.date_time),
      Type: row.type,
      Description: row.description,
      Amount: row.amount,
      Balance: row.running_balance,
    })),
  );
  const link = document.createElement("a");
  link.href = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
  );
  link.download = "statement.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap");
* {
  box-sizing: border-box;
}
/* Page chrome (header, buttons) lives in styles/list-page.scss — shared
   with Order History / Users / Payments. Only this page's table styles
   are below. */
.pg-list-wrap {
  padding-top: 4px;
  padding-bottom: 48px;
}
.pg-table {
  width: 100%;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}
.pg-thead {
  display: grid;
  grid-template-columns: 140px 160px 130px 1fr 140px 150px;
  background: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 0 20px;
}
.pg-th {
  font-size: 0.68rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 10px;
  white-space: nowrap;
}
.pg-th--amount,
.pg-th--balance {
  text-align: right;
}
.pg-tbody {
  display: flex;
  flex-direction: column;
}
.pg-row-wrap {
  border-bottom: 1px solid #f1f5f9;
}
.pg-row-wrap:last-child {
  border-bottom: none;
}
.pg-row {
  display: grid;
  grid-template-columns: 140px 160px 130px 1fr 140px 150px;
  align-items: center;
  padding: 0 20px;
  height: 52px;
  transition: background 0.12s;
}
.pg-row:hover {
  background: #f8fafc;
}
.pg-td {
  padding: 0 10px;
  overflow: hidden;
}
.pg-ref {
  font-size: 0.84rem;
  font-weight: 700;
  color: #12284a;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.pg-td-text {
  font-size: 0.83rem;
  color: #334155;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
.pg-td-muted {
  font-size: 0.81rem;
  color: #64748b;
  font-weight: 400;
  white-space: nowrap;
  display: block;
}
.pg-amount {
  font-size: 0.84rem;
  font-weight: 700;
  display: block;
  text-align: right;
  white-space: nowrap;
}
.pg-amount--pos {
  color: #15803d;
}
.pg-amount--neg {
  color: #dc2626;
}
.pg-balance {
  font-size: 0.84rem;
  font-weight: 600;
  color: #12284a;
  display: block;
  text-align: right;
  white-space: nowrap;
}
.pg-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.73rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1.5px solid currentColor;
  text-transform: capitalize;
}
.pg-type--credit {
  color: #15803d;
  background: #f0fdf4;
  border-color: #86efac;
}
.pg-type--debit {
  color: #dc2626;
  background: #fef2f2;
  border-color: #fca5a5;
}
.pg-type--default {
  color: #64748b;
  background: #f8fafc;
  border-color: #cbd5e1;
}
.pg-empty {
  text-align: center;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.pg-empty-icon {
  font-size: 2.5rem;
}
.pg-empty-text {
  font-size: 1rem;
  font-weight: 500;
  color: #94a3b8;
}

/* ── Pending-hold strip ───────────────────────────────────────
   Info-tone card that sits above the statement table when the
   accounting service reports funds held pending doc verification.
   Blue (not red/amber) because this is informational — the money is
   still the customer's, it just isn't spendable until docs pass. */
.pg-hold-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #eff6ff;
  border: 1.5px solid #bfdbfe;
  border-radius: 12px;
  color: #1e3a8a;
}
.pg-hold-strip__icon {
  width: 34px; height: 34px; border-radius: 9px;
  background: #dbeafe; color: #1d4ed8;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.pg-hold-strip__body {
  flex: 1; min-width: 0;
  display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px;
}
.pg-hold-strip__amount {
  font-size: 1rem; font-weight: 700; color: #12284a;
}
.pg-hold-strip__label {
  font-size: 0.85rem; color: #1e3a8a; font-weight: 500;
}
.pg-hold-strip__count {
  font-size: 0.75rem; font-weight: 600; color: #1d4ed8;
  background: #ffffff;
  border: 1.5px solid #bfdbfe; border-radius: 999px;
  padding: 4px 12px; flex-shrink: 0;
  text-transform: lowercase;
}

</style>
